import styled, { css } from 'styled-components';
import { mix } from 'polished';
import Tooltip from '../Tooltip';
import { tokens } from '../../../design/tokens';

interface ContainerProps {
  isFocused: boolean;
  hasValue: boolean;
  hasError: boolean;
  enabled: boolean;
  customBgColor: string | undefined;
  customRadius: string | undefined;
  customWidth: string | undefined;
}

export const Container = styled.div<ContainerProps>`
  background: ${props => (props.customBgColor ? props.customBgColor : tokens.color.fieldSlate)};

  border-radius: ${props => (props.customRadius ? `${props.customRadius}` : '8px')};

  display: flex;
  align-items: center;

  padding: 16px;
  width: ${props => (props.customWidth ? `${props.customWidth}` : '75%')};

  border: 1px solid ${tokens.color.fieldSlate};
  color: white;

  @media (max-width: 768px) {
    width: 100%;
  }

  & + div {
    margin-top: 16px;
  }
  box-shadow: none;

  transition: border 0.3s, color 0.3s;

  /* O alvo do hover da mensagem de erro e o icone de alerta, que nao e
     focavel — sem isto o texto do erro so existiria para quem usa mouse.
     Focar o campo (teclado ou toque) revela a mesma mensagem. */
  &:focus-within .tooltip-message {
    opacity: 1;
    pointer-events: auto;
  }

  /* O anel de teclado global cai no <input>, que nao tem borda nem fundo
     proprios: desenhava um retangulo apertado em volta do texto, DENTRO da
     caixa pintada, parecendo uma segunda caixa. Aqui ele muda de dono para o
     campo inteiro, que e o que o usuario enxerga como controle — o anel nao
     some, so passa a contornar a coisa certa (ver "A Regra do Anel
     Inviolavel" no DESIGN.md).
     Sob @supports porque ':has()' nao existe em WebView antigo, que e publico
     real deste produto: sem o teste, o 'outline: none' abaixo valeria sozinho
     e o campo ficaria SEM foco visivel nesses aparelhos. Sem ':has()' o anel
     original do <input> permanece — menos elegante, nunca ausente.
     ':focus-within' nao serve aqui: ele nao distingue teclado de mouse e
     traria de volta o halo no clique, que o sistema removeu de proposito. */
  @supports selector(:has(*)) {
    &:has(input:focus-visible) {
      outline: 2px solid ${tokens.color.focusAmber};
      outline-offset: 2px;
    }

    input:focus-visible {
      outline: none;
    }
  }

  > input input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  ${props =>
    props.isFocused &&
    css`
      border: 1px solid ${tokens.color.focusAmber};
      /* color muda o icone (svg usa currentColor) - nao o texto do
         input, que tem cor propria abaixo por razao de contraste. */
      color: ${tokens.color.focusAmber};
      box-shadow: inset 0 0 0 1px ${tokens.color.focusAmber};
    `}

  ${props =>
    props.hasValue &&
    css`
      border: 1px solid ${tokens.color.focusAmber};
      color: ${tokens.color.focusAmber};
    `}

  /* Depois de foco e preenchido de proposito: erro e a verdade mais urgente
     e precisa ganhar dos dois. Antes este bloco vinha primeiro, e um campo
     invalido voltava a mostrar borda ambar assim que recebia foco — a borda,
     que e o canal de estado do sistema, mentia exatamente no momento em que
     o usuario ia corrigir o erro. */
  ${props =>
    props.hasError &&
    css`
      border: 1px solid ${tokens.color.errorRed};
      color: ${tokens.color.errorRed};

      /* Ardosia empurrada para o vermelho, nao substituida por ele. O valor
         anterior era 'background: rgba(197,48,48,0.04)', que trocava o
         preenchimento inteiro por um vermelho de 4% — o campo perdia a
         ardosia e virava um buraco preto no formulario. */
      background: ${mix(0.08, tokens.color.errorRed, tokens.color.fieldSlate)};
      box-shadow: inset 0 0 0 1px ${tokens.color.errorRed};
    `}

  ${props =>
    !props.enabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}

  input {
    background: transparent;
    flex: 1;
    border: 0;
    font-size: 15px;
    font-family: ${tokens.font.body.family};

    /* Sem isto o input nao encolhe: todo item flex nasce com
       min-width: auto, e para <input> isso e a largura intrinseca
       (~180px). O conteudo estourava a largura do Container e
       empurrava o botao do olho para FORA da caixa pintada. */
    min-width: 0;

    /* Sem isto, um valor mais longo que a caixa e cortado cru, sem
       aviso nenhum — visivel em qualquer Input estreito (campos
       pareados, por exemplo). So ativa quando ja haveria corte; um
       campo que hoje cabe inteiro no espaco nunca muda. */
    overflow: hidden;
    text-overflow: ellipsis;

    /* Sempre branco, deliberadamente - nao herda o ambar do estado
       de foco/preenchido. Ambar sobre este fundo mede ~2:1 de
       contraste (falha AA); branco e a MELHOR cor de texto possivel
       aqui e ainda assim so alcanca AA porque o fundo foi escurecido
       (ver tokens.fieldSlate). O sinal de estado fica na borda. */
    color: ${tokens.color.textWhite};
    &::placeholder {
      color: ${tokens.color.textWhite};
    }

    &:disabled {
      /* O apagamento vem do Container; sem isto o navegador aplica o
         cinza proprio de campo desabilitado e o texto perde contraste. */
      color: ${tokens.color.textWhite};
      -webkit-text-fill-color: ${tokens.color.textWhite};
      opacity: 1;
    }
  }

  svg {
    margin-right: 16px;
    color: ${tokens.color.textWhite};
    flex-shrink: 0;
  }
`;

export const VisuallyHiddenLabel = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const Error = styled(Tooltip)`
  height: 20px;

  svg {
    margin-right: 0px;
  }

  span {
    /* O tooltip vinha centrado no icone de alerta ('left: 50%' + translate).
       O icone fica encostado na direita do campo, entao metade da caixa de
       160px projetava para fora — em telas estreitas a mensagem era cortada
       pelo 'overflow-x: hidden' do body, silenciosamente. Ancorado a direita
       ele nunca ultrapassa a borda do campo, em nenhuma largura. */
    left: auto;
    right: 0;
    transform: none;

    width: max-content;
    max-width: min(240px, calc(100vw - 32px));

    &::before {
      left: auto;
      right: 6px;
      transform: none;
    }
  }

`;

export const EyePassword = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  /* O padding aumenta a area de toque de 20x20 para ~36x36; as
     margens negativas cancelam esse crescimento no layout, entao a
     altura da linha nao muda. */
  padding: 8px;
  margin: -8px -8px -8px 4px;

  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover svg {
    color: ${tokens.color.focusAmber};
  }

  svg {
    margin-right: 0px;

    /* Clareado de #b4bad4, que media 2.96:1 sobre a Ardosia de Campo e
       ficava abaixo do minimo de 3:1 para elemento nao textual. Este tom
       da 3.4:1 e mantem o recuo em relacao ao icone semantico da esquerda,
       que e branco. */
    color: ${tokens.color.iconGray};

    transition: color ${tokens.motion.slow};
  }
`;
